import React, { useState, useRef, useEffect } from 'react';
import { SkillMatchEntry } from '../../../../utils/types/candi_bank_types';

interface SkillChipRowProps {
  label: string;
  emptyHint?: string;
  badgeBg: string;
  badgeText: string;
  skills: string[];
  skillMatches: SkillMatchEntry[];
  coverage: Record<string, number>;
  tiers: { label: string; key: string }[];
  onSkillsChange: (skills: string[]) => void;
  onMoveTo: (skill: string, targetTier: string) => void;
  onRemoveMatch: (skill: string, match: string) => void;
  isReindexing: boolean;
}

export const SkillChipRow: React.FC<SkillChipRowProps> = ({
  label,
  emptyHint,
  badgeBg,
  badgeText,
  skills,
  skillMatches,
  coverage,
  tiers,
  onSkillsChange,
  onMoveTo,
  onRemoveMatch,
  isReindexing,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [openMenuSkill, setOpenMenuSkill] = useState<string | null>(null);
  
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuSkill(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAdding) {
      inputRef.current?.focus();
    }
  }, [isAdding]);

  const handleAddSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const newSkill = inputValue.trim();
      if (!skills.includes(newSkill)) {
        onSkillsChange([...skills, newSkill]);
      }
      setInputValue('');
      setIsAdding(false);
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    onSkillsChange(skills.filter((s) => s !== skillToRemove));
    if (expandedSkills[skillToRemove]) {
      setExpandedSkills((prev) => ({ ...prev, [skillToRemove]: false }));
    }
  };

  const toggleExpand = (skill: string) => {
    setExpandedSkills((prev) => ({ ...prev, [skill]: !prev[skill] }));
  };

  const isCoverageLoaded = Object.keys(coverage).length > 0;

  return (
    <div className='flex flex-col sm:flex-row sm:items-start gap-2 py-1.5 border-b border-slate-100/80 last:border-0'>
      {/* Tier Label */}
      <div className='w-24 sm:w-28 flex-shrink-0 flex items-center gap-1.5 pt-0.5'>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold ${badgeBg} ${badgeText}`}>
          {label}
        </span>
        {isReindexing && (
          <span className='w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping' title='Re-indexing' />
        )}
      </div>

      {/* Chips Container */}
      <div className='flex-1 flex flex-wrap items-center gap-1.5 min-w-0'>
        {skills.length === 0 && !isAdding && (
          <span className='text-[11px] text-slate-400 italic py-0.5'>{emptyHint || 'None'}</span>
        )}

        {skills.map((skill) => {
          const isZeroCoverage = isCoverageLoaded && coverage[skill] === 0;
          const isExpanded = expandedSkills[skill];
          const matchesForSkill = skillMatches.find((m) => m.skill === skill)?.matches || [];
          const hasMatches = matchesForSkill.length > 0;

          return (
            <div key={skill} className='inline-flex flex-col'>
              <div
                className={`inline-flex items-center rounded-lg border text-xs font-semibold transition-all shadow-2xs ${
                  isZeroCoverage
                    ? 'border-amber-300 bg-amber-50 text-amber-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                <button
                  type='button'
                  onClick={() => toggleExpand(skill)}
                  className='px-2 py-0.5 flex items-center gap-1 hover:bg-slate-50 rounded-l-lg'
                  title={isZeroCoverage ? 'Nobody in your pool has this' : undefined}
                >
                  {isZeroCoverage && (
                    <span className='w-1.5 h-1.5 rounded-full bg-amber-500' />
                  )}
                  <span>{skill}</span>
                  {hasMatches && (
                    <svg className={`w-2.5 h-2.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                      <polyline points='6 9 12 15 18 9' />
                    </svg>
                  )}
                </button>

                <button
                  type='button'
                  onClick={() => removeSkill(skill)}
                  className='px-1 py-0.5 hover:bg-red-50 hover:text-red-600 text-slate-400 border-l border-slate-200/80 transition-colors'
                  title='Remove skill'
                >
                  <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <line x1='18' y1='6' x2='6' y2='18' />
                    <line x1='6' y1='6' x2='18' y2='18' />
                  </svg>
                </button>

                <div className='relative border-l border-slate-200/80 flex items-center'>
                  <button
                    type='button'
                    onClick={() => setOpenMenuSkill(openMenuSkill === skill ? null : skill)}
                    className='px-1 py-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-r-lg transition-colors'
                    title='Move tier'
                  >
                    <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                      <circle cx='12' cy='12' r='1' />
                      <circle cx='12' cy='5' r='1' />
                      <circle cx='12' cy='19' r='1' />
                    </svg>
                  </button>

                  {openMenuSkill === skill && (
                    <div ref={menuRef} className='absolute top-full right-0 mt-1 w-36 bg-white rounded-xl shadow-lg border border-slate-200 z-30 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-150'>
                      <div className='px-2.5 py-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100'>
                        Move to tier
                      </div>
                      {tiers.map((t) => (
                        <button
                          key={t.key}
                          className='w-full text-left px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors'
                          onClick={() => {
                            onMoveTo(skill, t.key);
                            setOpenMenuSkill(null);
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Phrasings expansion */}
              {isExpanded && hasMatches && (
                <div className='mt-1 ml-2 pl-2 border-l-2 border-indigo-200 flex flex-wrap gap-1'>
                  {matchesForSkill.map((match) => (
                    <div key={match} className='inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200/50'>
                      <span>{match}</span>
                      <button
                        onClick={() => onRemoveMatch(skill, match)}
                        className='text-slate-400 hover:text-red-500'
                        title='Reject phrasing'
                      >
                        <svg className='w-2.5 h-2.5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                          <line x1='18' y1='6' x2='6' y2='18' />
                          <line x1='6' y1='6' x2='18' y2='18' />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Add skill input/button */}
        {isAdding ? (
          <div className='inline-flex items-center bg-white border border-indigo-500 rounded-lg px-2 py-0.5 shadow-xs'>
            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleAddSkill}
              onBlur={() => {
                if (!inputValue.trim()) setIsAdding(false);
              }}
              placeholder='Skill name...'
              className='outline-none text-xs text-slate-800 w-24 placeholder:text-slate-400 bg-transparent'
            />
          </div>
        ) : (
          <button
            type='button'
            onClick={() => setIsAdding(true)}
            className='inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-dashed border-slate-300 text-[11px] font-bold text-slate-500 hover:text-indigo-600 hover:border-indigo-400 bg-slate-50/50 hover:bg-white transition-all'
          >
            <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
            Add
          </button>
        )}
      </div>
    </div>
  );
};
